import { Component } from '@theme/component';

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #frame = 0;
  #resizeObserver = new ResizeObserver(() => this.#scheduleLayout());

  connectedCallback() {
    super.connectedCallback();
    this.#observeSize();
    window.addEventListener('resize', this.#scheduleLayout);
    document.fonts.addEventListener('loadingdone', this.#scheduleLayout);
    document.fonts.ready.then(this.#scheduleLayout);
    this.#scheduleLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    window.removeEventListener('resize', this.#scheduleLayout);
    document.fonts.removeEventListener('loadingdone', this.#scheduleLayout);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeSize();
    this.#scheduleLayout();
  }

  #observeSize() {
    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(this);
    const firstItem = this.refs.content.firstElementChild;
    if (firstItem) this.#resizeObserver.observe(firstItem);
  }

  #scheduleLayout = () => {
    if (!this.isConnected || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.isConnected) this.#layout();
    });
  };

  #layout() {
    const { wrapper, content } = this.refs;
    const firstItem = content.firstElementChild;
    if (!firstItem) return;

    const width = this.clientWidth;
    const itemWidth = firstItem.getBoundingClientRect().width;
    if (width <= 0 || itemWidth <= 0) return;

    const gap = parseFloat(getComputedStyle(content).columnGap) || 0;
    const copies = Math.max(1, Math.ceil((width + gap) / (itemWidth + gap)));
    const configuredSpeed = Number(this.getAttribute('data-speed-factor'));
    const speedFactor = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 25;

    while (content.children.length > 1) content.lastElementChild.remove();

    for (let index = 1; index < copies; index++) {
      const item = firstItem.cloneNode(true);
      item.removeAttribute('ref');
      item.setAttribute('aria-hidden', 'true');
      item.setAttribute('inert', '');
      content.appendChild(item);
    }

    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }

    const duplicate = content.cloneNode(true);
    duplicate.removeAttribute('ref');
    duplicate.setAttribute('aria-hidden', 'true');
    duplicate.setAttribute('inert', '');
    for (const item of duplicate.querySelectorAll('[ref="marqueeItems[]"]')) {
      item.removeAttribute('ref');
    }
    wrapper.appendChild(duplicate);

    this.style.setProperty('--marquee-speed', `${Math.sqrt(copies) * speedFactor}s`);
    this.setAttribute('data-marquee-ready', '');
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
